import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 指定タスクID終了リクエストAPI
 * Note: 現状、手動運転タスク完了用に使用している
 */
export const useTaskCompleteAPI = (): {
  state: APIState;
  postTaskComplete: (
    vehicleId: string,
    scheduleId: string,
    taskId: string,
  ) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const postTaskCompleteAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      scheduleId: string,
      taskId: string,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/schedules/${scheduleId}/tasks/${taskId}/complete`,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postTaskComplete = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, scheduleId: string, taskId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_task_complete', { status: 'failed' }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await postTaskCompleteAPI(
          project!.id,
          environment!.environment_id,
          vehicleId,
          scheduleId,
          taskId,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_task_complete', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return false;
        }
        notifySuccess({
          message: t('api.fms.post_task_complete', { status: 'successfully' }),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, postTaskCompleteAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    postTaskComplete,
  };
};
